import {
  Color,
  Mesh,
  NoBlending,
  MeshBasicMaterial,
  NormalBlending,
  OrthographicCamera,
  PlaneGeometry,
  Scene,
  WebGLRenderTarget,
  type WebGLRenderer,
} from 'three';
import { PaintFieldGrid, VisualRandom } from '../../visual/index.js';
import {
  DEFAULT_PALETTE_ID,
  mixPigments,
  paletteColor,
  resolvePalette,
  type Rgb,
  type VisualPalette,
} from '../../visual/index.js';
import type {
  QualityContext,
  VisualFieldReader,
  VisualFrameContext,
  VisualMutation,
  VisualRenderer,
  VisualSignalEvent,
  WaveEvent,
} from '../../visual/index.js';
import { InstancedField, NOISE_GLSL } from './materials/InstancedField.js';

/**
 * Ink, thrown at the picture and left there.
 *
 * Not a second `SumiRenderer`. Sumi is a *brush*: it is about control, about the
 * tonal range between 濃墨 and 淡墨, about a mark that shows the hand that made
 * it. This is the opposite end of the same material — ink arriving with force,
 * splattering, dragging, pooling, and **staying**. Sumi paints a picture; this
 * covers a surface.
 *
 * ## What makes it different from everything before it
 *
 * Every other renderer in this project draws things that are alive and then
 * gone: a puff, a wash, a wavefront, a stroke, all of them on a lifetime, all of
 * them leaving an empty frame if the player stops. This one has a **memory**.
 *
 * A splat lands wet. While it is wet it spreads, bleeds at the edge, and is
 * affected by whatever it landed on. Then it dries — and at the moment it dries
 * it is *stamped into a texture that is never cleared*. The wet splat goes away;
 * the stain does not. Ten minutes into a song the accumulated texture is a
 * record of everywhere the player has struck, and two performances of the same
 * chart on the same seed end on visibly different pictures.
 *
 * That is the whole architecture, and it is deliberately the smallest thing that
 * could produce it:
 *
 *   - **Two render targets, used in turn.** One is what the frame is showing;
 *     the other is what the next update writes. They swap when the write is
 *     finished, so the texture being sampled is never the texture being
 *     drawn into. A single target was tried first and produced a defect that
 *     took two phases to pin down — see `updatePersistent`.
 *   - **A coarse CPU grid** carrying the same deposits, because the questions
 *     other renderers ask — "is there ink here?" — cannot be answered by reading
 *     back a GPU texture at sixty frames a second. Both are written from the
 *     same call, so they cannot disagree.
 *
 * ## Determinism
 *
 * Nothing here is stored in a replay. The target is rebuilt by replaying the
 * inputs: same timeline, same seed, same recipe, same splats in the same order,
 * same stains. The texture is a *cache of the performance*, not a record of it.
 */

const SPLAT_FRAGMENT = /* glsl */ `
precision highp float;

${NOISE_GLSL}

uniform float uTime;
uniform float uDetail;
uniform float uBleed;
uniform float uPremultiply;

varying vec2 vUv;
varying vec4 vLife;
varying vec4 vColor;
varying vec4 vShape;
varying float vAge;

/**
 * The shape of a splat.
 *
 * A soft circle stamp is the one thing this must not look like, so the outline
 * is built from four things that each break it: an angularly-modulated body so
 * the boundary is lobed rather than round, a tapering tongue along the direction
 * of travel, satellite droplets thrown off it, and an interior that is eaten
 * away by noise. What comes out has no axis of symmetry at all.
 */
void main() {
  vec2 p = (vUv - 0.5) * 2.0;
  float seed = vLife.z;
  float wetness = vShape.x;
  float drag = vShape.y;
  float breakup = vShape.z;
  float satellites = vShape.w;

  float angle = atan(p.y, p.x);
  float radius = length(p);

  // The body. Modulated by angle, so the boundary is lobed — this is what stops
  // it reading as a circle before anything else has been added.
  // Low frequency on purpose. At a higher one the body grew as many lobes as
  // the droplets have, and the whole splat read as a head of cauliflower —
  // several equal florets rather than one mass that broke up when it landed.
  float wobble = fbm(vec2(angle * 1.05, seed * 3.1), int(uDetail)) - 0.5;
  float ridges = fbm(vec2(angle * 3.7, seed * 1.9), int(uDetail)) - 0.5;
  float bodyRadius = 0.36 * (1.0 + wobble * 1.25 + ridges * 0.3);

  // Arms. The thing that actually makes a splat read as a splat: ink does not
  // stop at a smooth boundary, it throws thin points outward from wherever the
  // surface tension gave way first. Without these the mark is a lobed blob, and
  // a field of lobed blobs reads as popcorn however irregular each one is.
  // Their number and phase come from the seed; their length from how violently
  // the ink arrived, which is the same parameter that eats the interior.
  float armCount = 3.0 + floor(hash21(vec2(seed, 4.1)) * 5.0);
  float armPhase = hash21(vec2(seed, 9.7)) * 6.28318;
  float arms = pow(max(sin(angle * armCount + armPhase), 1e-4), 9.0);
  // Uneven. Equal arms at equal spacing is a star, and a star is as much of a
  // stamp as a circle is.
  arms *= 0.35 + fbm(vec2(angle * armCount * 0.5, seed * 2.2), 2) * 1.3;
  bodyRadius += arms * breakup * 0.38;
  float dist = radius - bodyRadius;

  // The tongue. Ink arriving at an angle does not stop where it lands; it runs
  // on, narrowing. Along +x, which the vertex shader has already rotated to the
  // direction the impact was travelling.
  if (drag > 0.01) {
    float along = clamp(p.x / max(drag, 0.001), 0.0, 1.0);
    // The base is clamped away from zero. 'along' reaches exactly 1 at the end
    // of the tongue, and pow(0.0, y) for a non-integer y is undefined in GLSL ES
    // — on this driver it yielded NaN, which propagated into the frame and then
    // through the bloom chain, turning the *entire composite* black. Nothing
    // about the picture looked wrong; the whole frame simply went out.
    float halfWidth = 0.23 * pow(max(1.0 - along, 1e-4), 1.15) + 0.02;
    float across = abs(p.y) - halfWidth;
    float beyond = max(0.0, p.x - drag) + max(0.0, -p.x);
    dist = min(dist, max(across, beyond));
  }

  // Droplets thrown clear of the main mark, biased forward along the drag.
  for (int index = 0; index < 5; index += 1) {
    if (float(index) >= satellites) break;
    float h1 = hash21(vec2(seed, float(index) * 7.3));
    float h2 = hash21(vec2(seed * 1.7, float(index) * 3.1));
    float h3 = hash21(vec2(seed * 2.3, float(index) * 5.9));
    float spray = h1 * 6.28318;
    float reach = 0.40 + h2 * 0.28;
    vec2 centre = vec2(cos(spray), sin(spray)) * reach + vec2(drag * 0.45 * h3, 0.0);
    // Lobed too. Round droplets were the one part of this that still read as a
    // stamp — a splat made of irregular blobs and five perfect circles announces
    // exactly how it was built.
    vec2 offset = p - centre;
    float dropAngle = atan(offset.y, offset.x);
    float dropWobble = fbm(vec2(dropAngle * 2.3, seed * 5.1 + float(index)), int(uDetail)) - 0.5;
    float dropRadius = (0.018 + h3 * 0.045) * (1.0 + dropWobble * 0.9);
    dist = min(dist, length(offset) - dropRadius);
  }

  // The edge. Wet ink bleeds into the surface; dry ink stops dead.
  float bleed = mix(0.015, 0.15, wetness) * uBleed;
  float ink = 1.0 - smoothstep(-bleed, bleed, dist);
  if (ink <= 0.0) discard;

  // The interior is not solid. Ink beads and pulls away from itself as it dries,
  // so the middle of a splat is full of holes at a scale much finer than its
  // outline.
  float grain = fbm(p * 5.5 + seed * 1.3, int(uDetail));
  ink *= 1.0 - breakup * smoothstep(0.42, 0.78, grain);
  // And it is not evenly loaded either.
  ink *= 0.72 + 0.28 * fbm(p * 2.3 + seed * 2.7, int(uDetail));

  // Nothing reaches the quad's edge. The lesson from the ink renderer before
  // this one: anything that does comes back cut off in a straight line.
  ink *= 1.0 - smoothstep(0.90, 1.0, radius);

  // Nothing leaves this shader unless it is a real number.
  //
  // A NaN here does not show up as a bad splat — it shows up as the *entire
  // frame going black*, because the bloom pass blurs it across the whole
  // downsample chain and a NaN written to an 8-bit target reads as zero. That
  // failure is invisible in the geometry and catastrophic in the composite, and
  // this shader has several places a NaN could come from: pow of zero to a
  // fractional power, atan at the exact origin, a division by a value that a
  // caller could make zero. A comparison is cheaper than auditing all of them,
  // and NaN fails every comparison it is put in.
  if (!(ink >= 0.003)) discard;

  // Premultiplied. This material is drawn into a persistent target and that
  // target is composited back over the frame, so the colour has to be carried
  // already multiplied by its own alpha — otherwise the composite multiplies by
  // alpha a second time, and a surface holding a lot of soft edges darkens the
  // entire field towards black.
  float alpha = clamp(ink * vColor.a, 0.0, 1.0);

  // The colour is guarded as well as the coverage. A NaN in the pigment passes
  // every test above — the coverage is a perfectly good number — and still
  // reaches the frame, where the bloom pass blurs it across everything.
  vec3 tint = vColor.rgb;
  if (!(tint.r >= 0.0) || !(tint.g >= 0.0) || !(tint.b >= 0.0)) discard;
  gl_FragColor = vec4(clamp(tint, 0.0, 4.0) * mix(1.0, alpha, uPremultiply), alpha);
}
`;

/** What kind of thing threw the ink. Each is a different *shape*, not a size. */
export type SplatKind = 'controlled' | 'burst' | 'smear' | 'explosive' | 'link';

/**
 * How far each kind of mark advances the palette cursor.
 *
 * Coprime with a five-colour palette wherever possible, so a run of one kind
 * cycles the whole palette instead of alternating between two entries. The
 * values differ per kind so that *how the run went* changes which colours come
 * up, which is the input §4 asks for — without turning the score itself into a
 * colour, which it explicitly rules out.
 */
const PIGMENT_STEP: Readonly<Record<SplatKind, number>> = {
  controlled: 1,
  burst: 3,
  smear: 2,
  explosive: 4,
  link: 1,
};

const KIND_CODE: Readonly<Record<SplatKind, number>> = {
  controlled: 0,
  burst: 1,
  smear: 2,
  explosive: 3,
  link: 4,
};

interface Splat {
  active: boolean;
  /** Field units. */
  x: number;
  y: number;
  angle: number;
  /** Field units. Grows while wet. */
  size: number;
  spread: number;
  born: number;
  /** 1 when it lands, 0 when it dries and is stamped. */
  wetness: number;
  dryingRate: number;
  drag: number;
  breakup: number;
  satellites: number;
  pigment: [number, number, number];
  opacity: number;
  seed: number;
  kind: SplatKind;
  /** Set the frame it is written into the persistent target. */
  stamped: boolean;
}

export interface InkPaintRendererOptions {
  readonly scene: Scene;
  readonly renderer: WebGLRenderer;
  readonly layerId: string;
  readonly fieldRadius: number;
  readonly capacity?: number;
}

/** How far past the playfield radius the paint surface reaches. */
const EXTENT = 1.15;

export class InkPaintRenderer implements VisualRenderer {
  readonly id = 'INK_PAINT';

  private readonly scene: Scene;
  private readonly gl: WebGLRenderer;
  private readonly fieldRadius: number;
  private readonly baseCapacity: number;
  private capacity: number;
  private detail = 3;
  private targetSize = 1024;

  /** The splats that are still wet, drawn live. */
  private wet: InstancedField | null = null;
  /** The same material, used once per splat to write it into the target. */
  private stamp: InstancedField | null = null;
  private stampScene: Scene | null = null;
  private stampCamera: OrthographicCamera | null = null;
  /**
   * The two persistent surfaces.
   *
   * `front` indexes the one the frame is showing; the other is the one an
   * update writes. See `updatePersistent` for why there are two.
   */
  private targets: [WebGLRenderTarget, WebGLRenderTarget] | null = null;
  private front = 0;
  /** Blits the front surface onto the back one before new paint is added. */
  private copyQuad: Mesh | null = null;
  private copyMaterial: MeshBasicMaterial | null = null;
  private surface: Mesh | null = null;
  private surfaceMaterial: MeshBasicMaterial | null = null;
  private fade: Mesh | null = null;

  private splats: Splat[] = [];
  private pending: Splat[] = [];
  private enabled = true;
  private lastTime = 0;
  private targetCleared = false;
  /**
   * Splats that dried this frame, waiting to be written into the surface.
   *
   * Held rather than written immediately, because the write has to happen
   * after the frame that samples the surface has been composited. See
   * `postRender`.
   */
  private readonly toStamp: Splat[] = [];
  private lastContext: VisualFrameContext | null = null;
  private pendingFade = 0;

  /**
   * The coverage map, published for other renderers.
   *
   * Public because the engine reads it to publish; nothing outside writes to it.
   */
  readonly fieldReader: VisualFieldReader;
  private readonly grid: PaintFieldGrid;

  // --- recipe-controlled behaviour -----------------------------------------
  /** How much of a dried stain survives. 1 keeps it for the whole run. */
  /**
   * The colours this layer paints with.
   *
   * Resolved once from the recipe and the seed, never per mark: a palette that
   * changed while a run was in progress would make the artwork a record of when
   * the renderer happened to re-read its settings rather than of the run.
   */
  private palette: VisualPalette = resolvePalette(DEFAULT_PALETTE_ID, 0);
  /**
   * Which colour the next mark takes.
   *
   * A counter rather than a random draw, walked by a per-event step. Random
   * picks clump — three reds in a row is common and looks like a bug — whereas
   * a stepped cycle guarantees the palette is actually *seen* while still
   * varying with the seed.
   */
  private pigmentCursor = 0;

  private persistence = 0.95;
  private dryingRate = 0.4;
  private bleedRate = 0.6;

  private impacts = 0;
  private stamps = 0;
  private readonly kindCounts: Record<string, number> = {};
  private readonly mutationCounts: Record<string, number> = {};

  constructor(private readonly options: InkPaintRendererOptions) {
    this.scene = options.scene;
    this.gl = options.renderer;
    this.fieldRadius = options.fieldRadius;
    this.baseCapacity = options.capacity ?? 48;
    this.capacity = this.baseCapacity;
    this.grid = new PaintFieldGrid({ size: 48, extent: EXTENT });
    this.fieldReader = this.grid;
  }

  setQuality(quality: QualityContext): void {
    if (this.wet === null) {
      this.capacity = Math.max(12, Math.round(this.baseCapacity * quality.capacityScale));
      // The persistent surface is the memory of the whole performance, so it is
      // the one buffer worth spending resolution on — but it is also the one
      // that costs whether or not anything is happening, so it is capped.
      this.targetSize =
        quality.preset === 'LIVE' ? 768 : quality.preset === 'HIGH' ? 1024 : 1536;
    }
    this.detail = Math.max(2, Math.min(6, Math.round(2 + quality.iterationScale * 1.4)));
    this.wet?.setUniform('uDetail', this.detail);
    this.stamp?.setUniform('uDetail', this.detail);
  }

  /** The seed a generated palette is built from. Set once, at initialise. */
  private paletteSeed = 0;

  initialize(context: VisualFrameContext): void {
    this.paletteSeed = context.random.seed;
    this.palette = resolvePalette(
      typeof context.layer.parameters.palette === 'string'
        ? context.layer.parameters.palette
        : undefined,
      this.paletteSeed,
    );
    if (this.wet === null) {
      const uniforms = (premultiply: number) => ({
        uDetail: { value: this.detail },
        uBleed: { value: this.bleedRate },
        uPremultiply: { value: premultiply },
      });

      this.wet = new InstancedField({
        scene: this.scene,
        name: `visual:INK_PAINT:${this.options.layerId}`,
        capacity: this.capacity,
        fragmentShader: SPLAT_FRAGMENT,
        blending: NormalBlending,
        // Straight alpha: this one draws into the main scene, which composites
        // that way. Only the round trip through the persistent target needs
        // premultiplied colour, and that is the stamp field below.
        premultipliedAlpha: false,
        renderOrder: 8,
        uniforms: uniforms(0),
      });

      // The stamp pass draws into the target, so it lives in its own scene with
      // its own camera. Reusing the main scene would mean the whole world was
      // re-rendered once per stamp.
      this.stampScene = new Scene();
      this.stamp = new InstancedField({
        scene: this.stampScene,
        name: 'ink:stamp',
        capacity: this.capacity,
        fragmentShader: SPLAT_FRAGMENT,
        blending: NormalBlending,
        premultipliedAlpha: true,
        renderOrder: 0,
        uniforms: uniforms(1),
      });
      this.stamp.attach();

      const half = this.fieldRadius * EXTENT;
      this.stampCamera = new OrthographicCamera(-half, half, half, -half, -1, 1);

      const makeTarget = (): WebGLRenderTarget =>
        new WebGLRenderTarget(this.targetSize, this.targetSize, {
          depthBuffer: false,
          stencilBuffer: false,
        });
      this.targets = [makeTarget(), makeTarget()];
      this.front = 0;

      // The blit. `NoBlending` makes it a straight copy — the destination has
      // just been cleared, and anything else would raise the question of
      // whether the source alpha is premultiplied.
      this.copyMaterial = new MeshBasicMaterial({
        map: this.targets[1].texture,
        transparent: true,
        blending: NoBlending,
        depthWrite: false,
        depthTest: false,
      });
      this.copyQuad = new Mesh(new PlaneGeometry(half * 2, half * 2), this.copyMaterial);
      this.copyQuad.frustumCulled = false;

      this.surfaceMaterial = new MeshBasicMaterial({
        map: (this.targets[this.front] as WebGLRenderTarget).texture,
        transparent: true,
        // The target was filled by blending, so what is in it is premultiplied.
        premultipliedAlpha: true,
        depthWrite: false,
        depthTest: false,
        blending: NormalBlending,
      });
      this.surface = new Mesh(new PlaneGeometry(half * 2, half * 2), this.surfaceMaterial);
      this.surface.frustumCulled = false;
      // Under the wet splats: dried ink is what the new ink lands on.
      this.surface.renderOrder = 7;
      this.surface.name = `visual:INK_PAINT_SURFACE:${this.options.layerId}`;

      // Used only when a recipe asks for a stain that fades.
      this.fade = new Mesh(
        new PlaneGeometry(half * 2, half * 2),
        new MeshBasicMaterial({
          color: 0x000000,
          transparent: true,
          premultipliedAlpha: true,
          opacity: 0,
          depthTest: false,
        }),
      );
      this.fade.frustumCulled = false;

      this.splats = Array.from({ length: this.capacity }, () => emptySplat());
    }

    this.wet.attach();
    if (this.surface !== null && this.surface.parent === null) this.scene.add(this.surface);
    if (!this.targetCleared) this.clearTarget();
    this.lastTime = context.time;
  }

  /** Both surfaces, cleared to transparent. */
  private clearTarget(): void {
    if (this.targets === null) return;
    const previousTarget = this.gl.getRenderTarget();
    // Saved and put back. Leaving the clear colour on transparent black wiped
    // the sky out of the whole frame the moment this renderer initialised —
    // the stage clears with it every frame, and this had quietly changed it.
    const previousColour = this.gl.getClearColor(new Color());
    const previousAlpha = this.gl.getClearAlpha();
    this.gl.setClearColor(0x000000, 0);
    for (const target of this.targets) {
      this.gl.setRenderTarget(target);
      this.gl.clear(true, false, false);
    }
    this.gl.setRenderTarget(previousTarget);
    this.gl.setClearColor(previousColour, previousAlpha);
    this.targetCleared = true;
  }

  reset(): void {
    for (const splat of this.splats) splat.active = false;
    this.pending.length = 0;
    this.wet?.clearAll();
    this.stamp?.clearAll();
    this.grid.clear();
    this.impacts = 0;
    this.stamps = 0;
    this.toStamp.length = 0;
    this.pendingFade = 0;
    // Back to the first surface, so a fresh run always starts on a known one.
    this.front = 0;
    if (this.surfaceMaterial !== null && this.targets !== null) {
      this.surfaceMaterial.map = this.targets[0].texture;
      this.surfaceMaterial.needsUpdate = true;
    }
    this.targetCleared = false;
    this.clearTarget();
    for (const key of Object.keys(this.kindCounts)) delete this.kindCounts[key];
    for (const key of Object.keys(this.mutationCounts)) delete this.mutationCounts[key];
  }

  setParameters(parameters: Readonly<Record<string, number | string | boolean>>): void {
    // Absent means the default palette, which is what keeps an older recipe
    // loading — it simply gains colour rather than failing to parse.
    const requested = typeof parameters.palette === 'string' ? parameters.palette : undefined;
    this.palette = resolvePalette(requested, this.paletteSeed);
    this.persistence = clamp01(numeric(parameters.persistence, this.persistence));
    this.dryingRate = Math.max(0.02, numeric(parameters.dryingRate, this.dryingRate));
    this.bleedRate = Math.max(0, numeric(parameters.bleedRate, this.bleedRate));
    this.wet?.setUniform('uBleed', this.bleedRate);
    this.stamp?.setUniform('uBleed', this.bleedRate);
  }

  handleSignal(event: VisualSignalEvent): void {
    // Without a wave layer this is how ink gets thrown: the press itself.
    if (event.name === 'NOTE_HIT') this.throwInk('controlled', event.strength, null);
    else if (event.name === 'NOTE_MISS') this.throwInk('burst', 1, null);
  }

  /**
   * How the run reaches the paint.
   *
   * The requirement here is that these differ in *shape*, not in size — four
   * marks of different diameters would say nothing about how the run was going.
   * So each kind changes the parameters that decide the outline: how far the
   * tongue runs, how many droplets are thrown, how eaten the interior is.
   */
  applyMutation(item: VisualMutation, context: VisualFrameContext): void {
    this.mutationCounts[item.action] = (this.mutationCounts[item.action] ?? 0) + 1;
    const struck = !item.sustained;
    const force = item.sustained ? item.strength * context.deltaTime : item.strength;

    switch (item.action) {
      case 'SPAWN':
        if (struck) this.throwInk('controlled', item.strength, null);
        break;
      case 'SPLASH':
        // A miss throws ink rather than placing it.
        if (struck) this.throwInk('burst', item.strength, null);
        break;
      case 'SHIFT':
        // An off-beat press drags: the hand was moving when the ink left it.
        if (struck) this.throwInk('smear', item.strength, null);
        break;
      case 'BREAK':
        if (struck) this.throwInk('explosive', item.strength, null);
        break;
      case 'MERGE':
        // A combo joins what is already there rather than adding somewhere new.
        if (struck) this.throwInk('link', item.strength, null);
        break;
      case 'BLEED':
        this.bleedRate = Math.min(2, this.bleedRate + force * 0.4);
        this.wet?.setUniform('uBleed', this.bleedRate);
        break;
      case 'FREEZE':
        this.dryingRate = Math.min(4, this.dryingRate + force * 0.8);
        break;
      case 'RELEASE':
        this.dryingRate = Math.max(0.05, this.dryingRate - force * 0.6);
        break;
      default:
        break;
    }
  }

  /**
   * A wave, as ink thrown at the surface.
   *
   * The style decides the shape, which is where the run becomes visible: a
   * struck note places a controlled mark, a miss bursts, an off-beat press
   * smears along the way the hand was going.
   */
  spawnWave(event: WaveEvent): void {
    const kind: SplatKind =
      event.style === 'torn' ? 'burst'
      : event.style === 'stray' ? 'smear'
      : event.style === 'swell' ? 'link'
      : 'controlled';
    this.throwInk(kind, event.strength, event);
  }

  private throwInk(kind: SplatKind, strength: number, event: WaveEvent | null): void {
    if (this.pending.length > 16) return;
    const splat = emptySplat();
    splat.kind = kind;
    splat.opacity = clamp01(strength);
    if (event !== null) {
      splat.x = event.x;
      splat.y = event.y;
      splat.angle = event.direction;
      // Reduced to something a shader can hold.
      //
      // A wave's seed is a 32-bit mix, so it arrives around a billion. The noise
      // in the fragment shader multiplies it, squares it and takes a dot product
      // of the result — which passes float32's ceiling and comes back infinite.
      // Every other material happens to avoid this by seeding a VisualRandom and
      // drawing a small number out of it; this one passed the raw value through,
      // and the consequence was not a bad splat but a black *frame*: the
      // infinity reached the bloom pass and the blur spread it over everything.
      splat.seed = (event.seed >>> 0) % 1000 / 7.3;
      splat.size = event.scale;
    } else {
      // No wave: the caller has no opinion about where. Marked with a NaN seed
      // so `update` knows to draw a position from the layer's own stream.
      splat.seed = Number.NaN;
      splat.size = 1;
    }
    this.pending.push(splat);
  }

  update(context: VisualFrameContext): void {
    const wet = this.wet;
    if (wet === null || !this.enabled) return;

    const delta = Math.min(0.1, Math.max(0, context.time - this.lastTime));
    this.lastTime = context.time;

    // The recipe may have changed under us.
    this.persistence = clamp01(numeric(context.layer.parameters.persistence, this.persistence));
    this.dryingRate = Math.max(0.02, numeric(context.layer.parameters.dryingRate, this.dryingRate));

    for (const request of this.pending) this.land(context, request);
    this.pending.length = 0;

    this.grid.advance(delta, { drying: this.dryingRate, persistence: this.persistence });

    const opacity = context.layer.opacity * context.layer.intensity;
    const drying: Splat[] = [];
    let live = 0;

    for (const splat of this.splats) {
      if (!splat.active) continue;

      // Wet ink spreads. It slows as it dries, so the mark settles rather than
      // simply stopping — the same asymptote the washes use.
      splat.wetness = Math.max(0, splat.wetness - splat.dryingRate * delta);
      splat.size += splat.spread * delta * splat.wetness;

      if (splat.wetness <= 0.01) {
        // Dry. It stops being a live object and becomes part of the surface.
        drying.push(splat);
        splat.active = false;
        continue;
      }

      this.write(wet, live, splat, opacity);
      live += 1;
      if (live >= wet.capacity) break;
    }

    wet.setTime(context.time);
    wet.commit(live);

    // Queued, not written. The full-field quad that displays the surface is
    // in this scene and samples the target, so writing it here would put the
    // write before that read inside one frame. See `postRender`.
    for (const splat of drying) this.toStamp.push({ ...splat, wetness: 0 });
    this.lastContext = context;
    this.pendingFade += delta;
  }

  /** Places one splat, reading what it landed on. */
  private land(context: VisualFrameContext, request: Splat): void {
    const splat = this.splats.find((candidate) => !candidate.active);
    if (splat === undefined) return;
    const stream = Number.isFinite(request.seed)
      ? new VisualRandom(request.seed | 0)
      : context.random;
    const reach = numeric(context.layer.parameters.reach, 0.72);

    splat.active = true;
    splat.born = context.time;
    splat.kind = request.kind;
    splat.seed = Number.isFinite(request.seed) ? request.seed % 137 : stream.next() * 100;
    splat.x = Number.isFinite(request.seed) ? request.x : stream.signed() * reach;
    splat.y = Number.isFinite(request.seed) ? request.y : stream.signed() * reach * 0.85;
    splat.angle = Number.isFinite(request.seed) ? request.angle : stream.range(0, Math.PI * 2);

    // What is already here. This is the interaction: ink behaves differently on
    // wet paper than on dry, and differently again on top of existing pigment.
    const under = context.field.sample(splat.x, splat.y);

    const force = clamp01(request.opacity);
    // A wide spread of sizes. Marks that are all one size read as a pattern,
    // and the pattern is the first thing that gives away a stamp.
    const base = (0.45 + force * 0.25) * stream.range(0.55, 1.5);

    switch (request.kind) {
      case 'burst':
        // A miss. Large, irregular, thrown wide, with the interior torn out.
        splat.size = base * 1.7 * request.size;
        splat.spread = 0.16;
        splat.drag = stream.range(0.1, 0.3);
        splat.breakup = stream.range(0.5, 0.85);
        splat.satellites = 5;
        splat.dryingRate = this.dryingRate * 0.7;
        break;
      case 'smear':
        // Off-beat. Small head, long tongue, almost no droplets — the mark of a
        // hand that was still moving.
        splat.size = base * 1.15 * request.size;
        splat.spread = 0.07;
        splat.drag = stream.range(0.75, 1.05);
        splat.breakup = stream.range(0.15, 0.35);
        splat.satellites = 1;
        splat.dryingRate = this.dryingRate * 1.3;
        break;
      case 'explosive':
        // A combo break. Everything at once, and it flies.
        splat.size = base * 2.1 * request.size;
        splat.spread = 0.28;
        splat.drag = stream.range(0.2, 0.5);
        splat.breakup = stream.range(0.7, 0.95);
        splat.satellites = 5;
        splat.dryingRate = this.dryingRate * 0.55;
        break;
      case 'link': {
        // A combo. Reaches towards the nearest paint instead of landing clear of
        // it, so a run of them grows one connected region rather than a scatter.
        splat.size = base * 1.1 * request.size;
        splat.spread = 0.1;
        splat.drag = stream.range(0.45, 0.8);
        splat.breakup = stream.range(0.1, 0.3);
        splat.satellites = 2;
        splat.dryingRate = this.dryingRate;
        // Points at the paint it is joining.
        splat.angle = this.towardsPaint(context, splat.x, splat.y, splat.angle);
        break;
      }
      default:
        // A clean hit. Compact, few droplets, intact interior.
        splat.size = base * request.size;
        splat.spread = 0.055;
        splat.drag = stream.range(0.05, 0.2);
        splat.breakup = stream.range(0.2, 0.42);
        splat.satellites = 2;
        splat.dryingRate = this.dryingRate * 1.1;
        break;
    }

    // Landing in wet paint: it spreads further, bleeds longer and its boundary
    // is pulled about by what is already flowing there.
    if (under.wetness > 0.05) {
      splat.spread *= 1 + under.wetness * 1.6;
      splat.dryingRate *= 1 - under.wetness * 0.4;
      splat.breakup = clamp01(splat.breakup * (1 - under.wetness * 0.5));
    }

    splat.wetness = 1;
    splat.opacity = clamp01(0.55 + force * 0.4);
    splat.pigment = this.mixPigment(context, under, force, splat.kind);

    this.impacts += 1;
    this.kindCounts[splat.kind] = (this.kindCounts[splat.kind] ?? 0) + 1;
  }

  /**
   * The colour this splat ends up.
   *
   * ## Where the colour comes from
   *
   * A palette entry, chosen by a cursor that steps by an amount derived from
   * the mark's own kind and seed. That gives three things at once: several
   * pigments present in one artwork rather than one colour per run, a
   * distribution that actually visits the whole palette, and a sequence that is
   * a pure function of the seed — so the same performance repaints identically.
   *
   * The kinds step by different amounts on purpose. A run of clean hits walks
   * the palette one way; a run broken by misses walks it another, so two
   * performances of the same chart come out different colours rather than the
   * same colours in a different arrangement.
   *
   * ## Where it stops coming from
   *
   * It used to be `[0.19, 0.20, 0.24]` — a neutral grey with a saturation
   * around 0.1 — with an optional `hue` parameter nothing set. The material was
   * not being desaturated by the pipeline; it was being *written* grey, and that
   * is the whole of why INK_PAINT looked like white paint.
   *
   * ## Mixing
   *
   * Wet paint joins: ink landing in ink that has not set ends between the two.
   * Dry paint mixes as well, but weakly — a mark laid over a dry one picks up
   * some of it the way a wash of pigment over dried pigment does, which is what
   * makes an overlap read as a third colour rather than as the newer mark
   * simply covering the older. `mixPigments` is what decides the hue; it is not
   * an average, because averaging red and blue gives a muddy grey rather than
   * the purple somebody expects to see.
   */
  private mixPigment(
    context: VisualFrameContext,
    under: { readonly wetness: number; readonly pigment: readonly [number, number, number] },
    force: number,
    kind: SplatKind,
  ): [number, number, number] {
    const hue = numeric(context.layer.parameters.hue, Number.NaN);
    let own: Rgb;
    if (Number.isFinite(hue)) {
      // An explicit hue still wins, for a recipe that names one.
      own = hslToRgb(((hue % 1) + 1) % 1, 0.85, 0.16 + force * 0.1);
    } else {
      this.pigmentCursor += PIGMENT_STEP[kind];
      own = paletteColor(this.palette, this.pigmentCursor);
      // Force lifts the value a little without touching the hue, so a hard
      // impact reads as more pigment rather than as a different colour. Kept
      // small: the measured tone curve turns anything much brighter white.
      const lift = 1 + force * 0.22;
      own = [
        Math.min(1, (own[0] as number) * lift),
        Math.min(1, (own[1] as number) * lift),
        Math.min(1, (own[2] as number) * lift),
      ];
    }

    const underPigment = under.pigment as Rgb;
    const underDensity = Math.max(
      Math.max(underPigment[0], underPigment[1]),
      underPigment[2],
    );
    // Nothing underneath worth mixing with.
    if (underDensity <= 0.02) return own as [number, number, number];

    const influence = this.palette.backgroundInfluence ?? 0.35;
    // Wet paint blends far more than dry, which is the difference a viewer
    // reads as "these two ran together" versus "this one was laid over that".
    const amount = under.wetness > 0.05
      ? influence * (0.45 + under.wetness * 0.55)
      : influence * 0.4;
    const mixed = mixPigments(own, underPigment, amount);
    return [mixed[0], mixed[1], mixed[2]];
  }

  /** The direction of the nearest painted region, for `link`. */
  private towardsPaint(
    context: VisualFrameContext,
    x: number,
    y: number,
    fallback: number,
  ): number {
    let bestAngle = fallback;
    let best = 0;
    // Eight probes on a ring. Enough to find a neighbour, cheap enough to do on
    // every combo mark; a search over the whole grid would be neither.
    for (let index = 0; index < 8; index += 1) {
      const angle = (index / 8) * Math.PI * 2;
      const probe = context.field.sample(x + Math.cos(angle) * 0.22, y + Math.sin(angle) * 0.22);
      if (probe.density > best) {
        best = probe.density;
        bestAngle = angle;
      }
    }
    return bestAngle;
  }

  private write(field: InstancedField, index: number, splat: Splat, opacity: number): void {
    field.write(
      index,
      [splat.x * this.fieldRadius, splat.y * this.fieldRadius, splat.size * this.fieldRadius, splat.angle],
      [splat.born, 1, splat.seed, KIND_CODE[splat.kind]],
      [splat.pigment[0], splat.pigment[1], splat.pigment[2], clamp01(splat.opacity * opacity)],
      [clamp01(splat.wetness), splat.drag, splat.breakup, splat.satellites],
    );
  }

  /**
   * Moves the persistent paint forward by one update, onto the other surface.
   *
   * ## Why there are two surfaces
   *
   * There was one, and it produced a defect that survived two phases. Writing
   * to a render target and then compositing a frame that involves that target
   * produced **one black frame per write** — the whole frame, background and
   * all. Phase 6C found it during play and moved the write after the composite,
   * which fixed play only because consecutive composites there are a whole
   * frame apart. Offline, where frames run back to back, it came straight back:
   * a stamp on frame 59 blacked frame 60, and the count of black frames was
   * always exactly the count of writes.
   *
   * Everything about *when* was tried and none of it helped — flushing the GPU,
   * yielding to the host between frames, hiding the quad that displays the
   * surface. What was never tried was not reusing the same texture, and that is
   * what this does: the frame samples one surface while the update writes the
   * other, and they change places only once the write is finished. No texture
   * is ever the destination of a write and the source of a read in the same
   * frame, which is the condition WebGL leaves undefined.
   *
   * ## The order
   *
   * Read the front, write the back, swap. The blit is what carries the history
   * across, and it is why the two surfaces stay identical in content: paint is
   * only ever added, never read back and modified, so a copy plus the new marks
   * is the whole of the state.
   */
  private updatePersistent(context: VisualFrameContext, opacity: number): void {
    const stamp = this.stamp;
    const scene = this.stampScene;
    const camera = this.stampCamera;
    const targets = this.targets;
    const copy = this.copyQuad;
    if (stamp === null || scene === null || camera === null || targets === null || copy === null) {
      return;
    }

    const read = targets[this.front] as WebGLRenderTarget;
    const write = targets[1 - this.front] as WebGLRenderTarget;

    // Write the dried splats into the stamp field, and deposit the same marks
    // into the queryable grid. Both come from this loop, so the picture and the
    // answers about it cannot drift apart.
    let count = 0;
    for (const splat of this.toStamp) {
      if (count >= stamp.capacity) break;
      this.write(stamp, count, splat, opacity);
      count += 1;
      this.grid.deposit({
        x: splat.x,
        y: splat.y,
        radius: splat.size * 0.45,
        density: splat.opacity * opacity,
        wetness: 0,
        pigment: splat.pigment,
      });
    }

    const fading = this.persistence < 1 && this.pendingFade > 0;
    if (count === 0 && !fading) return;

    stamp.setTime(context.time);
    stamp.commit(count);

    const previousTarget = this.gl.getRenderTarget();
    const previousAutoClear = this.gl.autoClear;
    const previousColour = this.gl.getClearColor(new Color());
    const previousAlpha = this.gl.getClearAlpha();

    this.gl.autoClear = false;
    this.gl.setRenderTarget(write);
    this.gl.setClearColor(0x000000, 0);
    this.gl.clear(true, false, false);

    // 1. Everything painted so far, copied across from the surface the frame is
    //    currently showing.
    if (this.copyMaterial !== null) {
      this.copyMaterial.map = read.texture;
      this.copyMaterial.needsUpdate = true;
    }
    scene.add(copy);
    this.gl.render(scene, camera);
    scene.remove(copy);

    // 2. The new marks, over the top.
    if (count > 0) this.gl.render(scene, camera);

    // 3. The stain letting go, if the recipe asked for that.
    if (fading) {
      const fade = this.fade;
      if (fade !== null) {
        const amount = (1 - this.persistence) * 0.35 * this.pendingFade;
        (fade.material as MeshBasicMaterial).opacity = Math.min(0.2, amount);
        scene.add(fade);
        this.gl.render(scene, camera);
        scene.remove(fade);
      }
    }

    this.gl.setRenderTarget(previousTarget);
    this.gl.setClearColor(previousColour, previousAlpha);
    this.gl.autoClear = previousAutoClear;

    stamp.commit(0);
    this.stamps += count;
    this.toStamp.length = 0;
    this.pendingFade = 0;

    // 4. They change places. Only now does the frame start sampling what was
    //    just written, and by then nothing is writing to it.
    this.front = 1 - this.front;
    if (this.surfaceMaterial !== null) {
      this.surfaceMaterial.map = (targets[this.front] as WebGLRenderTarget).texture;
      this.surfaceMaterial.needsUpdate = true;
    }
  }
  render(): void {
    // Nothing: the surface and the wet splats are in the main scene and the
    // stage draws them. The persistent write happens in postRender.
  }

  /**
   * Writes what dried into the persistent surface, after the frame is on screen.
   *
   * This ordering is the whole of the fix for a defect that cost a great deal
   * of time. The stamp used to happen during update, which meant that on any
   * frame where a splat dried the target was written and then — later in the
   * same frame — sampled by the full-field quad that displays it. Sampling a
   * texture that was rendered to earlier in the same frame is undefined, and
   * what came back was opaque black across a quad covering the whole view: the
   * sky went with it, and only on frames where something dried. Off-beat input
   * makes the fastest-drying splat, so off-beat play flickered black while
   * clean play looked perfect.
   *
   * Running after the composite makes it read-then-write: this frame shows
   * what the last one produced, and the write lands where nothing can sample
   * it again until the next frame.
   */
  postRender(): void {
    const context = this.lastContext;
    if (context === null || !this.enabled) return;
    const opacity = context.layer.opacity * context.layer.intensity;

    this.updatePersistent(context, opacity);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (this.wet !== null) this.wet.visible = enabled;
    if (this.surface !== null) this.surface.visible = enabled;
  }

  dispose(): void {
    this.wet?.dispose();
    this.stamp?.dispose();
    for (const target of this.targets ?? []) target.dispose();
    this.targets = null;
    this.copyMaterial?.dispose();
    this.copyQuad?.geometry.dispose();
    this.copyQuad = null;
    this.copyMaterial = null;
    this.surfaceMaterial?.dispose();
    if (this.surface !== null) {
      this.surface.geometry.dispose();
      this.scene.remove(this.surface);
    }
    if (this.fade !== null) {
      this.fade.geometry.dispose();
      (this.fade.material as MeshBasicMaterial).dispose();
    }
    this.wet = null;
    this.stamp = null;
    this.surface = null;
    this.fade = null;
    this.splats = [];
  }

  get metrics(): {
    readonly wetSplats: number;
    readonly capacity: number;
    /** Impacts landed over the run. */
    readonly impacts: number;
    /** Splats written into the persistent surface. */
    readonly stamps: number;
    /** 0..1, how much of the picture has been painted. */
    readonly coverage: number;
    readonly wetness: number;
    readonly persistence: number;
    readonly dryingRate: number;
    readonly bleedRate: number;
    readonly targetSize: number;
    /** How many persistent surfaces are allocated. Two, since the ping-pong. */
    readonly targetCount: number;
    /** Bytes of GPU memory the persistent surfaces occupy. */
    readonly targetBytes: number;
    readonly kinds: Readonly<Record<string, number>>;
    readonly mutations: Readonly<Record<string, number>>;
  } {
    return {
      wetSplats: this.wet?.liveCount ?? 0,
      capacity: this.capacity,
      impacts: this.impacts,
      stamps: this.stamps,
      coverage: Number(this.grid.coverage.toFixed(4)),
      wetness: Number(this.grid.wetness.toFixed(4)),
      persistence: Number(this.persistence.toFixed(3)),
      dryingRate: Number(this.dryingRate.toFixed(3)),
      bleedRate: Number(this.bleedRate.toFixed(3)),
      targetSize: this.targetSize,
      targetCount: this.targets?.length ?? 0,
      targetBytes: (this.targets?.length ?? 0) * this.targetSize * this.targetSize * 4,
      kinds: { ...this.kindCounts },
      mutations: { ...this.mutationCounts },
    };
  }
}

function emptySplat(): Splat {
  return {
    active: false, x: 0, y: 0, angle: 0, size: 0.1, spread: 0.03, born: 0,
    wetness: 1, dryingRate: 0.4, drag: 0.1, breakup: 0.2, satellites: 2,
    pigment: [0.3, 0.31, 0.36], opacity: 0.8, seed: 0, kind: 'controlled', stamped: false,
  };
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
  const m = l - c / 2;
  const sector = Math.floor(h * 6) % 6;
  const rgb: [number, number, number] =
    sector === 0 ? [c, x, 0]
    : sector === 1 ? [x, c, 0]
    : sector === 2 ? [0, c, x]
    : sector === 3 ? [0, x, c]
    : sector === 4 ? [x, 0, c]
    : [c, 0, x];
  return [rgb[0] + m, rgb[1] + m, rgb[2] + m];
}

function numeric(value: number | string | boolean | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
